import Head from 'next/head'
import { CacheProvider } from '@emotion/react'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { CssBaseline, CircularProgress, Box } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { AuthConsumer, AuthProvider } from 'src/contexts/auth-context'
import { useNProgress } from 'src/hooks/use-nprogress'
import { createTheme } from 'src/theme'
import { createEmotionCache } from 'src/utils/create-emotion-cache'
import 'simplebar-react/dist/simplebar.min.css'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import axios from 'axios'
import '../styles/globals.scss'
import useStore from 'src/store/store'

const clientSideEmotionCache = createEmotionCache()

const SplashScreen = () => null

const App = (props) => {
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)

  const isLoginPage = router.pathname === '/login'

  // 現状isAuthenticatedはここでしか使っていないので、Zustandによる状態管理は不要だが利用方法のサンプルとして書いている
  const isAuthenticated = useStore((state) => state.isAuthenticated)
  const setIsAuthenticated = useStore((state) => state.setIsAuthenticated)

  useEffect(() => {
    const pathname = router.pathname // 現在のパスを取得

    // ログインページのパスであれば、認証チェックをスキップ
    if (isLoginPage) {
      return
    }

    const checkAuth = async () => {
      try {
        setIsLoading(true)
        await axios.get(`${process.env.NEXT_PUBLIC_API_URL}/auth/check`, {
          withCredentials: true,
        })
        setIsAuthenticated(true)
      } catch (error) {
        console.error('Error:', error)
        if (error.response.status === 401) {
          // 認証エラーの場合、ログインページにリダイレクト
          router.push('/login')
        }
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [router])

  // Service Worker の登録。Android / デスクトップ Chrome の「インストール」導線は
  // fetch ハンドラを持つ SW を要件にしているため登録する（オフライン対応はしない）。
  // 開発時は next dev の HMR と衝突するので production ビルドのみ。
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return
    if (!('serviceWorker' in navigator)) return

    navigator.serviceWorker.register('/sw.js').catch(() => {
      // 登録に失敗してもホーム画面に追加できないだけで、管理画面の利用には影響しない
    })
  }, [])

  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props

  useNProgress()

  const getLayout = Component.getLayout ?? ((page) => page)

  const theme = createTheme()

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <title>Pittalinco</title>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
        <meta name="robots" content="noindex" />
      </Head>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        (isLoginPage || isAuthenticated) && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <AuthProvider>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <AuthConsumer>{(auth) => (auth.isLoading ? <SplashScreen /> : getLayout(<Component {...pageProps} />))}</AuthConsumer>
              </ThemeProvider>
            </AuthProvider>
          </LocalizationProvider>
        )
      )}
    </CacheProvider>
  )
}

export default App
